/* ==========================================================
   Grumpi Sale Badge — variant change handler
   ----------------------------------------------------------
   Keeps BOTH the price-side pill AND the image overlay in
   sync with the selected variant's custom.discount_label
   metafield.

   Why a MutationObserver?
   -----------------------
   This is a Dawn-based theme. The product info area is a
   <product-info> web component that re-fetches and re-renders
   itself whenever the shopper switches variant. The price-side
   badge lives INSIDE that component, so the theme updates it
   for free. The image overlay badge lives OUTSIDE it (in the
   gallery container) and is therefore NEVER re-rendered on a
   variant change — only on a full page reload. That was the
   "lag until refresh" you saw.

   So instead of guessing which variant-change event the theme
   fires, we simply watch <product-info> for re-renders and
   re-sync the overlay the instant it happens. By the time the
   re-render fires, the ?variant= URL and the hidden [name="id"]
   input already hold the new variant, so we read the correct
   label.

   Reads variant labels from a JSON tag rendered by
   snippets/grumpi-sale-badge-variant-data.liquid:
     { "__product_default": "...", "<variant_id>": "...", ... }

   Resolution order:
     1. variant-level value (if non-empty)
     2. product-level value (__product_default)
     3. otherwise empty -> hide badge
   ========================================================== */

(function () {
  'use strict';

  function readVariantLabels() {
    var node = document.querySelector('script[type="application/json"][data-grumpi-variant-labels]');
    if (!node) return null;
    try {
      return JSON.parse(node.textContent || '{}');
    } catch (err) {
      return null;
    }
  }

  function getCurrentVariantId() {
    // The hidden id input is updated by the theme after a variant change.
    var input = document.querySelector('form[action*="/cart/add"] [name="id"]');
    if (input && input.value) return String(input.value);

    var url = new URL(window.location.href);
    return url.searchParams.get('variant') || null;
  }

  // Overlay badges that belong to the PDP (i.e. NOT inside a product card).
  function pdpOverlayBadges() {
    return Array.prototype.slice
      .call(document.querySelectorAll('[data-grumpi-badge="overlay"]'))
      .filter(function (el) {
        return !el.closest('.product-card') && !el.closest('.card.product-card');
      });
  }

  function priceBadge() {
    return document.querySelector('[data-grumpi-badge="price"]');
  }

  function updateBadges() {
    var labels = readVariantLabels();
    if (!labels) return;

    var vid = getCurrentVariantId();
    var variantLabel = vid && labels[vid] ? labels[vid] : '';
    var fallback = labels.__product_default || '';
    var nextLabel = variantLabel || fallback;

    // Price-side badge — update text + visibility.
    var pBadge = priceBadge();
    if (pBadge) {
      if (nextLabel) {
        pBadge.textContent = nextLabel;
        pBadge.style.display = '';
      } else {
        pBadge.style.display = 'none';
      }
    }

    // Image overlay (PDP only) — update text + visibility so it matches
    // the selected variant's discount_label metafield.
    pdpOverlayBadges().forEach(function (el) {
      if (nextLabel) {
        el.textContent = nextLabel;
        el.style.display = '';
      } else {
        el.style.display = 'none';
      }
    });
  }

  // Collapse a burst of mutations (a single re-render fires many) into one
  // update on the next animation frame, after the DOM has settled.
  var scheduled = null;
  function scheduleUpdate() {
    if (scheduled) cancelAnimationFrame(scheduled);
    scheduled = requestAnimationFrame(function () {
      scheduled = null;
      updateBadges();
    });
  }

  function init() {
    // Initial paint — handles deep-linked ?variant= URLs.
    updateBadges();

    // Primary mechanism: watch the product info component(s) for the
    // theme's own re-render on variant change.
    var infos = document.querySelectorAll('product-info, .product__info, #MainProduct, .product__info-container');
    if (infos.length && 'MutationObserver' in window) {
      var observer = new MutationObserver(scheduleUpdate);
      infos.forEach(function (el) {
        observer.observe(el, { childList: true, subtree: true });
      });
    }

    // Belt-and-braces fallbacks — harmless if they never fire.
    ['variant:change', 'product:variant-change', 'on:variant:change'].forEach(function (evt) {
      document.addEventListener(evt, scheduleUpdate);
    });
    document.addEventListener('change', function (e) {
      var t = e.target;
      if (t && (t.name === 'id' || (t.closest && t.closest('variant-selects')))) {
        scheduleUpdate();
      }
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
